import { cn } from "@/lib/utils";
import { useCRMMetadata } from "@/providers/crm";
import { useEventAlertContext } from "@/providers/event-alert";
import { SFEvent } from "@/types/salesforce/event";
import * as LucideIcons from "lucide-react";
import { Calendar, X } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface AlertBannerProps {
    alert: SFEvent;
    onRemove: (alertId: string) => void;
}

// Son de notification (utilise l'API Web Audio)
const playNotificationSound = () => {
    // Disable for now as it is too loud
    // try {
    //     const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    //     const oscillator = audioContext.createOscillator();
    //     const gainNode = audioContext.createGain();
    //     oscillator.connect(gainNode);
    //     gainNode.connect(audioContext.destination);
    //     oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    //     oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    //     gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    //     gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    //     oscillator.start(audioContext.currentTime);
    //     oscillator.stop(audioContext.currentTime + 0.3);
    // } catch (error) {
    //     console.warn("Impossible to play notification sound:", error);
    // }
};

export function AlertBanner({ alert, onRemove }: AlertBannerProps) {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const bannerRef = useRef<HTMLDivElement>(null);

    // Metadata pour les icônes et couleurs
    const { metadata: opportunitiesMetadata } = useCRMMetadata("opportunities");
    const { metadata: leadsMetadata } = useCRMMetadata("leads");
    const { metadata: accountsMetadata } = useCRMMetadata("accounts");
    const { metadata: casesMetadata } = useCRMMetadata("cases");

    // Déterminer les métadonnées selon le type d'objet lié
    const getMetadata = () => {
        if (alert.opportunity) return opportunitiesMetadata;
        if (alert.lead) return leadsMetadata;
        if (alert.account) return accountsMetadata;
        if (alert.case) return casesMetadata;
        return null;
    };

    const metadata = getMetadata();
    const iconName = metadata?.model_metadata?.icon || "CalendarDays";
    const iconColor = metadata?.model_metadata?.color || "#64748b";

    // Récupérer l'icône Lucide correspondante
    const LucideIcon = (LucideIcons as any)[iconName] || Calendar;

    // Calculer le temps restant
    const getTimeRemaining = () => {
        if (!alert.start_datetime) return "now";

        const now = DateTime.now();
        const startTime = DateTime.fromISO(alert.start_datetime);
        const diff = startTime.diff(now, "minutes");

        const minutes = Math.floor(diff.minutes);
        const seconds = Math.floor(diff.seconds % 60);

        if (minutes > 0) {
            return `in ${minutes} minute${minutes > 1 ? "s" : ""}`;
        }
        if (seconds > 0) {
            return `in ${seconds} second${seconds > 1 ? "s" : ""}`;
        }
        return "now";
    };

    // Vérifier si l'événement est imminent (moins d'1 minute)
    const isImminent = () => {
        if (!alert.start_datetime) return false;

        const now = DateTime.now();
        const startTime = DateTime.fromISO(alert.start_datetime);
        const diff = startTime.diff(now, "minutes");

        return diff.minutes < 1;
    };

    // État pour l'animation d'attention (bounce toutes les 5 secondes)
    const [showAttentionBounce, setShowAttentionBounce] = useState(false);

    // Gérer le clic sur la bannière
    const handleClick = () => {
        // Construire l'URL de redirection selon le type d'objet lié
        let redirectUrl = "";
        if (alert.opportunity) {
            redirectUrl = `/opportunities/${alert.opportunity.id}/informations`;
        } else if (alert.lead) {
            redirectUrl = `/leads/${alert.lead.id}/informations`;
        } else if (alert.account) {
            redirectUrl = `/accounts/${alert.account.id}/informations`;
        } else if (alert.case) {
            redirectUrl = `/cases/${alert.case.id}/informations`;
        } else if (alert.recycling_marker) {
            redirectUrl = `/recycling_markers/${alert.recycling_marker.id}/informations`;
        } else {
            redirectUrl = `/events/${alert.id}/informations`;
        }

        navigate(redirectUrl);
        onRemove(alert.id.toString());
    };

    // Gérer la fermeture manuelle
    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsRemoving(true);
        setTimeout(() => {
            onRemove(alert.id.toString());
        }, 300); // Délai pour l'animation de sortie
    };

    // Auto-suppression après 15 minutes
    useEffect(() => {
        if (!alert.start_datetime) return;

        const now = DateTime.now();
        const startTime = DateTime.fromISO(alert.start_datetime);
        const timeSinceStart = now.diff(startTime, "minutes").minutes;

        if (timeSinceStart >= 15) {
            onRemove(alert.id.toString());
            return;
        }

        const timeUntilExpiry = (15 - timeSinceStart) * 60 * 1000; // en millisecondes
        timeoutRef.current = setTimeout(() => {
            onRemove(alert.sf_id || alert.id.toString());
        }, timeUntilExpiry);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [alert, onRemove]);

    // Animation d'entrée et son
    useEffect(() => {
        // Jouer le son de notification
        playNotificationSound();

        // Changer le titre de l'onglet
        const originalTitle = document.title;
        document.title = `🔔 ${alert.subject} | Walter CRM`;

        // Animation d'entrée
        const timer = setTimeout(() => setIsVisible(true), 100);

        return () => {
            clearTimeout(timer);
            document.title = originalTitle;
        };
    }, [alert.subject]);

    // Mettre à jour le temps restant toutes les secondes
    const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining());
    const [previousTimeRemaining, setPreviousTimeRemaining] = useState(timeRemaining);

    useEffect(() => {
        const interval = setInterval(() => {
            const newTimeRemaining = getTimeRemaining();
            setTimeRemaining(newTimeRemaining);

            // Rejouer le son quand l'événement commence
            if (previousTimeRemaining !== "now" && newTimeRemaining === "now") {
                playNotificationSound();
            }

            setPreviousTimeRemaining(newTimeRemaining);
        }, 1000);

        return () => clearInterval(interval);
    }, [previousTimeRemaining, alert.start_datetime]);

    // Animation d'attention : bounce toutes les 5 secondes quand imminent
    useEffect(() => {
        if (!alert.start_datetime) return;

        // Animation simple : bounce toutes les 5 secondes si l'événement n'est pas terminé
        const interval = setInterval(() => {
            if (isImminent()) {
                setShowAttentionBounce(true);
                setTimeout(() => {
                    setShowAttentionBounce(false);
                }, 200);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [alert.start_datetime]); // Supprimé timeRemaining pour éviter les re-déclenchements

    if (!isVisible && isRemoving) return null;

    return (
        <div
            ref={bannerRef}
            className={cn(
                "fixed right-4 top-4 z-50 max-w-sm transform transition-all duration-300 ease-out",
                isVisible && !isRemoving ? "translate-x-0 scale-100 opacity-100" : "translate-x-full scale-95 opacity-0",
                // Animation d'attention : pulse doux toutes les 5 secondes quand imminent
                showAttentionBounce && "scale-[102%] shadow-2xl duration-300"
            )}
            onClick={handleClick}
        >
            {/* Languette principale */}
            <div className="relative cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl">
                {/* Barre de progression colorée en haut */}
                <div className="h-1 w-full" style={{ backgroundColor: iconColor }} />

                {/* Contenu de la bannière */}
                <div className="p-4">
                    <div className="flex items-center gap-3">
                        {/* Icône */}
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white" style={{ backgroundColor: iconColor }}>
                            <LucideIcon className="h-6 w-6" />
                        </div>
                        <div className="mr-8">
                            <h3 className=" text-sm font-semibold leading-tight text-gray-900">{alert.subject}</h3>
                            {/* Temps restant */}
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{timeRemaining}</span>
                            </div>
                        </div>
                        {/* Bouton fermer */}
                        <button
                            onClick={handleClose}
                            className="absolute right-4 top-4 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 transition-colors duration-200 hover:bg-gray-200"
                        >
                            <X className="h-3 w-3 text-gray-600" />
                        </button>
                    </div>
                    {/* Contenu principal */}
                    <div className="mt-2 min-w-0 flex-1">{alert.description && <p className="line-clamp-2 text-sm leading-relaxed text-gray-600">{alert.description}</p>}</div>
                </div>
            </div>

            {/* Ombre portée pour l'effet de surimpression */}
            <div className="pointer-events-none absolute inset-0 rounded-lg" />
        </div>
    );
}

// Composant conteneur pour gérer plusieurs alertes
export function AlertBannerContainer() {
    const { alerts, removeAlert } = useEventAlertContext();

    if (alerts.length === 0) return null;

    return (
        <div className="fixed right-0 top-0 z-50 space-y-3 p-4">
            {alerts.map(alert => (
                <AlertBanner key={alert.sf_id || alert.id.toString()} alert={alert} onRemove={removeAlert} />
            ))}
        </div>
    );
}
